import { useEffect, useRef } from "react";

/**
 * Sync any <Select/AsyncSelect> component's local "selected" state
 * with the value held inside your `addedFilters`.
 *
 * @param {object}   cfg
 * @param {string}   cfg.filterKey            key inside addedFilters   (e.g. "createdBy")
 * @param {Array}    cfg.options              [{ value, label }, ...]   - may arrive async
 * @param {object}   cfg.addedFilters         your filters object
 * @param {Function} cfg.setSelected          React setter from useState
 * @param {boolean}  [cfg.isMulti=false]      true ➜ multi‑select mode
 * @param {Function} [cfg.getFilterValue]     how to read the value from filter object
 */

export function useSelectSync({
  filterKey,
  options,
  addedFilters,
  setSelected,
  selected,
  isMulti = false,
  getFilterValue = (f) => (f ? f.value : null),
}) {
  const prevRawRef = useRef(undefined);

  useEffect(() => {
    const filter = addedFilters?.[filterKey];
    const rawValue = getFilterValue(filter);

    // ---------- fast‑exit only if:  (a) raw value unchanged AND (b) it is already resolvable ----------
    const unchanged = prevRawRef.current === JSON.stringify(rawValue);
    const resolvable =
      !rawValue ||
      (Array.isArray(rawValue)
        ? rawValue.every((v) => options.some((o) => o.value === v))
        : options.some((o) => o.value === rawValue));

    const alreadySelected = !rawValue
      ? !selected
      : isMulti
      ? Array.isArray(selected) &&
        rawValue.every((v) => selected.some((s) => s.value === v))
      : selected?.value === rawValue;

    if (unchanged && resolvable && alreadySelected) return; // ✅ safe to skip

    prevRawRef.current = JSON.stringify(rawValue);

    // ---------- clear if no value ----------
    if (!rawValue || (Array.isArray(rawValue) && rawValue.length === 0)) {
      setSelected(null);
      return;
    }

    // ---------- map raw ➜ option(s) ----------
    if (isMulti && Array.isArray(rawValue)) {
      const found = rawValue
        .map((v) => options.find((opt) => opt.value === v))
        .filter(Boolean);
      setSelected(found.length ? found : null);
    } else {
      console.log("options: ", options);
      const found = options.find((opt) => opt.value === rawValue);
      console.log("found: ", found);

      if (found) setSelected(found);
    }
  }, [options, addedFilters, filterKey, isMulti, setSelected, getFilterValue]);
}

/* -------------------------------------------------------------------------- */
/* 🌟 NEW: keep a plain text input (search box) in sync with addedFilters     */
/* -------------------------------------------------------------------------- */

/**
 * Sync a simple text input’s state with `addedFilters`.
 *
 * @param {string}   filterKey     key inside addedFilters (e.g. "name")
 * @param {object}   addedFilters  your filters object
 * @param {Function} setValue      React setter from useState for that input
 */
export function useInputSync(filterKey, addedFilters, setValue) {
  const prev = useRef("");

  useEffect(() => {
    const raw = addedFilters?.[filterKey]?.value || "";

    if (prev.current === raw) return; // nothing changed
    prev.current = raw;
    setValue(raw); // push into input state
  }, [filterKey, addedFilters, setValue]);
}

export const useDateSync = (filterKey, addedFilters, setDateState) => {
  const prev = useRef("");
  useEffect(() => {
    const iso = addedFilters?.[filterKey]?.value || "";
    if (prev.current === iso) return;
    prev.current = iso;
    setDateState(iso ? new Date(iso) : null);
  }, [filterKey, addedFilters, setDateState]);
};
